/**
 * ÖSD Zertifikat B1 blog post: Redemittel Schreiben & Sprechen (PDF).
 * Covers the ZDÖ B1 (the format of our ÖSD B1 mock exams): intro, email Redemittel, practice, FAQ.
 */
import React, { useState } from 'react';
import { ChevronDown, HelpCircle } from 'lucide-react';
import RelatedResources from './RelatedResources';
import SprechenSamplesCallout from './SprechenSamplesCallout';
import RedemittelPhraseTables from './RedemittelPhraseTables';

const BLOG_FAQ_ITEMS = [
  {
    question: 'Is the ÖSD B1 the same as the Goethe-Zertifikat B1?',
    answer: 'Not quite. ÖSD runs two B1 exams. The ZDÖ B1 (Zertifikat Deutsch Österreich) is ÖSD\'s own exam: Lesen, Sprachbausteine, Hören and Schreiben form one written part, and the oral exam is graded separately. ÖSD also offers the Zertifikat B1 together with the Goethe-Institut, which uses the Goethe B1 module format. Logami\'s ÖSD B1 mock exams and these Redemittel follow the ZDÖ B1, so check which exam your registration names.',
  },
  {
    question: 'How many points do I need to pass the ÖSD B1?',
    answer: 'The written part is worth 225 points: Lesen 75, Sprachbausteine 30, Hören 75 and Schreiben 45. You pass it with at least 135 points (60%), and there is no separate minimum for each subtest, so a strong Lesen score can make up for a weaker Hören. The oral exam is worth 75 points and is graded separately; you need at least 45.',
  },
  {
    question: 'Does ÖSD accept Austrian-specific German (Österreichisches Deutsch)?',
    answer: 'Absolutely. In fact, the ÖSD specifically recognizes Austrian and Swiss variants of Standard German as equally correct, which is a unique advantage of this certification.',
  },
];

function BlogFaqItem({ question, answer, isOpen, onClick }) {
  return (
    <div className="group mb-4">
      <div
        className={`
          relative bg-white border-2 rounded-2xl cursor-pointer overflow-hidden
          transition-all duration-500
          ${isOpen
            ? 'border-black shadow-2xl scale-[1.02]'
            : 'border-black/10 hover:border-black/30 hover:shadow-xl hover:scale-[1.01]'
          }
        `}
        onClick={onClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onClick()}
        aria-expanded={isOpen}
      >
        <div className={`
          absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-gray-50
          transition-all duration-500
          ${isOpen ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}
        `} />
        <div className="relative z-10 p-6 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 flex-1">
            <div className={`
              relative flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center
              transition-all duration-500
              ${isOpen
                ? 'bg-black text-white scale-110 rotate-6'
                : 'bg-black/5 text-black group-hover:bg-black group-hover:text-white group-hover:scale-110'
              }
            `}>
              <HelpCircle className="w-6 h-6 relative z-10" />
            </div>
            <div className="relative flex-1">
              <h3 className={`
                font-space text-lg font-bold transition-all duration-300
                ${isOpen ? 'text-black' : 'text-black/80 group-hover:text-black'}
              `}>
                {question}
              </h3>
              <div className={`
                absolute bottom-0 left-0 h-0.5 bg-black transition-all duration-500
                ${isOpen ? 'w-full' : 'w-0 group-hover:w-1/3'}
              `} />
            </div>
          </div>
          <div className="flex-shrink-0">
            <ChevronDown className={`
              w-6 h-6 transition-all duration-500
              ${isOpen ? 'rotate-180 text-black scale-125' : 'rotate-0 text-black/50 group-hover:text-black group-hover:scale-110'}
            `} />
          </div>
        </div>
        <div className={`
          relative z-10 overflow-hidden transition-all duration-500
          ${isOpen ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0'}
        `}>
          <div className="px-6 pb-6 pt-2">
            <div className="relative h-px bg-black/10 mb-4 overflow-hidden">
              <div className={`
                absolute inset-0 bg-gradient-to-r from-transparent via-black to-transparent
                transition-transform duration-1000
                ${isOpen ? 'translate-x-0' : '-translate-x-full'}
              `} />
            </div>
            <p className="text-black/70 leading-relaxed text-base md:text-lg max-w-none">
              {answer}
            </p>
          </div>
        </div>
        {isOpen && (
          <>
            <div className="absolute top-0 left-0 w-16 h-16 border-t-2 border-l-2 border-black/20 rounded-tl-2xl pointer-events-none" />
            <div className="absolute bottom-0 right-0 w-16 h-16 border-b-2 border-r-2 border-black/20 rounded-br-2xl pointer-events-none" style={{ animationDelay: '0.5s' }} />
          </>
        )}
      </div>
    </div>
  );
}

const OsdB1RedemittelPost = ({ pdfUrl }) => {
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  return (
    <article className="blog-post-content">
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-8">
        Logami&apos;s ÖSD B1 mock exams follow the <strong>ÖSD Zertifikat Deutsch Österreich B1 (ZDÖ B1)</strong>. Its
        written part is worth 225 points (Leseverstehen 75, Sprachbausteine 30, Hörverstehen 75 and Schreiben 45),
        and you pass it with at least 135. The oral exam, worth 75 points, is graded separately. Redemittel matter
        most in <em>Schreiben</em>, where you write one email, formal or informal, and in the oral exam with a
        partner. Below are the phrases sorted by task, with English meanings and examples, and the full list is a
        free PDF.
      </p>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        Essential Redemittel for ÖSD B1: Formal and Informal Emails
      </h2>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-4">
        The <em>Redemittel ÖSD B1</em> you need first are for Schreiben, which is a single email. The task tells you
        whether it is formal or informal, and that register decides your phrases: a formal email opens with
        &quot;Sehr geehrte Damen und Herren,&quot; and closes with &quot;Mit freundlichen Grüßen&quot;, an informal one
        opens with &quot;Liebe Anna,&quot; and closes with &quot;Viele Grüße&quot;. Mixing the two is one of the most
        common mistakes at B1.
      </p>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-6">
        Whichever email you get, you will usually have to give your opinion and ask for something. Phrases such as
        &quot;Ich finde es wichtig, dass...&quot; or &quot;Meiner Meinung nach sollte man...&quot; cover the first;
        &quot;Ich schreibe Ihnen, weil...&quot; or &quot;Könnten Sie mir bitte mitteilen, ob...&quot; cover the second.
        ÖSD also treats Austrian and Swiss variants of Standard German as correct, so regional words such as
        &quot;Jänner&quot; are not mistakes.
      </p>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        Practising the Phrases Under Exam Conditions
      </h2>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-6">
        A phrase list helps most when you can recall it under time pressure. Learn the phrases from the tables below
        or the free PDF, then use them in a timed answer. Logami is built by German teachers and has 4 complete ÖSD B1 mock exams
        in the official format, gives AI feedback on your written answers, and includes a Redemittel trainer that
        brings each phrase back just before you are likely to forget it. Your first mock exam is free.
      </p>

      {pdfUrl && (
        <p className="text-base md:text-lg text-black/70 leading-relaxed mb-8">
          If you want the complete collection of ÖSD-specific B1 phrases, you can download the PDF for the full Redemittel with examples for formal and informal emails and the oral exam.{' '}
          <a
            href={pdfUrl}
            download={pdfUrl.split('/').pop()}
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-black underline underline-offset-2 hover:opacity-80"
          >
            Download the full ÖSD B1 Redemittel PDF here
          </a>
          .
        </p>
      )}

      <RedemittelPhraseTables
        provider="osd"
        level="b1"
        examName="ÖSD B1"
        pdfUrl={pdfUrl}
      />

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-12 mb-6">
        Frequently Asked Questions
      </h2>
      <div className="space-y-3">
        {BLOG_FAQ_ITEMS.map((item, index) => (
          <BlogFaqItem
            key={index}
            question={item.question}
            answer={item.answer}
            isOpen={openFaqIndex === index}
            onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
          />
        ))}
      </div>

      <p className="mt-10 pt-6 border-t border-black/10 text-sm text-black/50">
        <strong>Last Updated:</strong> October 7, 2026
      </p>
      <SprechenSamplesCallout provider="osd" level="b1" />
      <RelatedResources provider="osd" level="b1" variant="redemittel" />

    </article>
  );
};

export default OsdB1RedemittelPost;
